import DesignButton from 'components/button/DesignButton';
import DesignInput from 'components/input/DesignInput';
import DesignSelect from 'components/select/DesignSelect';
import { AgencySettings, Courier, ProviderInfo } from 'core/graphql/types';

import { useState } from 'react';

interface ProviderInfoSelection {
  label: string;
  value: string | null | undefined;
}

export interface EditableSettingsCardProps {
  entityId: any;
  availableDefaultProviders: ProviderInfo[];
  availableCourierDesignedProviders: ProviderInfo[];
  providersAreLoading: boolean;
  entity: Courier | AgencySettings;

  onSave: (
    id: any,
    eta: string | undefined,
    defaultProvider: string | null | undefined,
    courierDesignedProvider: string | null | undefined,
  ) => void;
}

const EditableSettingsCard = ({
  entity,
  entityId,
  availableDefaultProviders,
  availableCourierDesignedProviders,
  providersAreLoading,
  onSave,
}: EditableSettingsCardProps) => {
  const digitsOnlyRegex = /[^0-9]+/g;

  const [editMode, setEditMode] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const availableDefaultProvidersValues = availableDefaultProviders.map(
    (x) => ({ value: x.providerName, label: x.providerDescription }),
  );
  const availableCourierDesignedProvidersValues =
    availableCourierDesignedProviders.map((x) => ({
      value: x.providerName,
      label: x.providerDescription,
    }));

  const selectedDefaultProvider = availableDefaultProvidersValues.filter(
    (x) => x.value === entity.defaultItineraryProvider,
  )[0];
  const selectedCourierDesignedProvider =
    availableCourierDesignedProvidersValues.filter(
      (x) => x.value === entity.courierDesignedItineraryProvider,
    )[0];
  const selectedEta =
    entity.etaInterval === undefined
      ? undefined
      : entity.etaInterval?.toString();

  const [etaValue, setEtaValue] = useState<string | undefined>(selectedEta);
  const [defaultItineraryProvider, setDefaultProviderValue] =
    useState<ProviderInfoSelection>(selectedDefaultProvider);
  const [courierDesignedItineraryProvider, setCourierDesignedProviderValue] =
    useState<ProviderInfoSelection>(selectedCourierDesignedProvider);

  const handleDefaultProviderChanged = (provider: ProviderInfoSelection) => {
    setDefaultProviderValue(provider);
  };

  const handleCourierDesignedProviderChanged = (
    provider: ProviderInfoSelection,
  ) => {
    setCourierDesignedProviderValue(provider);
  };

  const changeEditMode = () => {
    if (!editMode) {
      const defaultProvider = availableDefaultProvidersValues.filter(
        (x) => x.value === entity.defaultItineraryProvider,
      )[0];
      const courierDesignedProvider =
        availableCourierDesignedProvidersValues.filter(
          (x) => x.value === entity.courierDesignedItineraryProvider,
        )[0];
      const eta =
        entity.etaInterval === undefined
          ? undefined
          : entity.etaInterval?.toString();

      setEtaValue(eta);
      setDefaultProviderValue(defaultProvider);
      setCourierDesignedProviderValue(courierDesignedProvider);
    }

    setEditMode(!editMode);
  };

  const saveChanges = () => {
    console.log(etaValue);
    console.log(defaultItineraryProvider);
    console.log(courierDesignedItineraryProvider);

    if (
      etaValue !== entity.etaInterval?.toString() ||
      defaultItineraryProvider.value !== entity.defaultItineraryProvider ||
      courierDesignedItineraryProvider.value !==
        entity.courierDesignedItineraryProvider
    ) {
      onSave(
        entityId,
        etaValue === undefined ? '0' : etaValue.replace(digitsOnlyRegex, ''),
        defaultItineraryProvider.value,
        courierDesignedItineraryProvider.value,
      );
    }
    setEditMode(!editMode);
  };

  const onEtaChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setEtaValue(e.target.value);
    const invalidCharacters = e.target.value.match(digitsOnlyRegex);
    if (invalidCharacters) {
      setErrorMessage(`Input must not include ${invalidCharacters}`);
    } else {
      setErrorMessage('');
    }
  };

  return (
    <div>
      {editMode ? (
        <>
          <div className="flex w-[24rem] flex-col justify-around">
            <div>
              <label htmlFor="agencySelect">ETA</label>
              <DesignInput
                value={etaValue}
                type="number"
                name="customETA"
                step={1}
                onChanceHandler={onEtaChanged}
                placeHolder="ETA"
                className="mt-2"
              />
            </div>
            <div>
              <label htmlFor="agencySelect">Default Itinerary Provider:</label>
              <DesignSelect
                isLoading={providersAreLoading}
                value={defaultItineraryProvider}
                onChange={handleDefaultProviderChanged}
                options={availableDefaultProvidersValues}
                isMulti={false}
                closeMenuOnSelect
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="agencySelect">Custom Itinerary Provider:</label>
              <DesignSelect
                isLoading={providersAreLoading}
                value={courierDesignedItineraryProvider}
                onChange={handleCourierDesignedProviderChanged}
                options={availableCourierDesignedProvidersValues}
                isMulti={false}
                closeMenuOnSelect
                className="w-full"
              />
            </div>
            <div className="flex flex-row">
              <DesignButton onClickHandler={saveChanges} className="mr-2">
                Save
              </DesignButton>

              <DesignButton onClickHandler={changeEditMode}>
                Cancel
              </DesignButton>
            </div>
          </div>
          <p className="text-xs text-red">{errorMessage}</p>
        </>
      ) : (
        <div>
          <div className="flex w-[48rem] flex-row justify-between">
            <div>
              <label htmlFor="ETA">{`ETA: ${
                selectedEta === undefined ? 'N/A' : selectedEta
              }`}</label>
            </div>
            <div>
              <label htmlFor="DefaultProvider">{`Default Provider: ${
                selectedDefaultProvider === null
                  ? 'N/A'
                  : selectedDefaultProvider.label
              }`}</label>
            </div>
            <div>
              <label htmlFor="CustomProvider">{`Custom Provider: ${
                selectedCourierDesignedProvider === null
                  ? 'N/A'
                  : selectedCourierDesignedProvider.label
              }`}</label>
            </div>
            <div>
              <DesignButton
                onClickHandler={changeEditMode}
                className="w-[10rem]"
              >
                Edit
              </DesignButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditableSettingsCard;
